'use client'

import OmniDialog from '@/components/OmniDialog'
import ViverseButton from '@/components/ViverseButton'
import ViverseRadio from '@/components/ViverseRadio'
import ViverseTextarea from '@/components/ViverseTextarea'
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form'
import { REPORT_REASON_VALUES, ReportReason } from '@/constants/comment'
import { useConfigsContext } from '@/context/ConfigsContext'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslations } from 'next-intl'
import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

type ReportModalProps = {
  commentId: string
  isDialogOpen: boolean
  onSubmit: (commentId: string, reason: ReportReason, detail: string) => void
  onClose: () => void
}

const DETAIL_MAX_LENGTH = 500

const formSchema = z.object({
  reason: z.enum(REPORT_REASON_VALUES),
  detail: z.string().max(DETAIL_MAX_LENGTH),
})

export default function ReportModal({
  commentId,
  isDialogOpen,
  onSubmit,
  onClose,
}: ReportModalProps) {
  const t = useTranslations()
  const { store } = useConfigsContext()

  const initialValues = useMemo(() => ({ reason: undefined, detail: '' }), [])
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    defaultValues: initialValues,
  })

  const reportReasonOptions = useMemo(
    () =>
      REPORT_REASON_VALUES.map((value) => ({
        value,
        label: t(`report-modal.reasons.${value}`),
      })),
    [t],
  )

  const handleClose = () => {
    onClose()
    form.reset(initialValues)
  }

  const submitReport = async (values: z.infer<typeof formSchema>) => {
    const htcAuthKey = store?.credentials?.htcToken
    if (!htcAuthKey) return

    onSubmit(commentId, values.reason, values.detail)
    handleClose()
  }

  const isSubmitDisabled = !form.formState.isValid

  return (
    <OmniDialog
      title={t('report-modal.title')}
      description={t('report-modal.description')}
      open={isDialogOpen}
      onClose={handleClose}
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(submitReport)} noValidate className="w-full space-y-4">
          <FormField
            control={form.control}
            name="reason"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ViverseRadio
                    onValueChange={field.onChange}
                    value={field.value}
                    options={reportReasonOptions}
                    optionRowClassName="py-0"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="detail"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ViverseTextarea
                    {...field}
                    containerClassName="border-none"
                    className="bg-vive-primary-70 text-vive-primary-30"
                    placeholder={t('report-modal.detail-placeholder')}
                    showMaxLength={false}
                    maxLength={DETAIL_MAX_LENGTH}
                    rows={4}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="flex w-full justify-center gap-4">
            <ViverseButton type="button" variant="secondary" onClick={handleClose}>
              {t('report-modal.cancel')}
            </ViverseButton>
            <ViverseButton
              type="submit"
              disabled={isSubmitDisabled}
              aria-disabled={isSubmitDisabled}
            >
              {t('report-modal.report')}
            </ViverseButton>
          </div>
        </form>
      </Form>
    </OmniDialog>
  )
}
